import React, { useState, useMemo } from 'react';

const nav = [
  { id: 'overview',    icon: 'bx-grid-alt',         label: 'Overview' },
  { id: 'audience',    icon: 'bx-group',            label: 'Audience' },
  { id: 'acquisition', icon: 'bx-target-lock',      label: 'Acquisition' },
  { id: 'behavior',    icon: 'bx-mouse',            label: 'Behavior' },
  { id: 'technology',  icon: 'bx-devices',          label: 'Technology' },
  { id: 'events',      icon: 'bx-pulse',            label: 'Events' },
  { id: 'revenue',     icon: 'bx-dollar-circle',    label: 'Revenue' },
  { id: 'funnel',      icon: 'bx-filter-alt',       label: 'Funnel' },
  { id: 'custom',      icon: 'bx-customize',        label: 'Custom Report' },
  { id: 'insights',    icon: 'bx-bot',              label: 'AI Insights' },
];

const APP_COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24','#a78bfa','#22d3ee'];

const PLATFORMS = [
  { id: 'all',     label: 'All',     icon: 'bx-grid' },
  { id: 'android', label: 'Android', icon: 'bxl-android', logo: true },
  { id: 'ios',     label: 'iOS',     icon: 'bxl-apple',   logo: true },
];

const Icon = ({ name, style, logo }) => (
  <i className={`${logo ? 'bx' : 'bx'} ${name}`} style={style} />
);

export default function Sidebar({ active, onChange, properties, selectedProp, onPropChange, theme, onToggleTheme, mobileOpen, onMobileClose }) {
  const [appOpen,   setAppOpen]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [platform,  setPlatform]  = useState('all');

  const currentProp = properties.find(p => p.id === selectedProp) || properties[0];
  const globalIdx   = properties.findIndex(p => p.id === selectedProp);
  const accentColor = APP_COLORS[globalIdx >= 0 ? globalIdx % APP_COLORS.length : 0];

  const platformCounts = useMemo(() => {
    const counts = { all: properties.length, android: 0, ios: 0 };
    properties.forEach(p => {
      const pl = (p.platform || 'android').toLowerCase();
      if (pl === 'android') counts.android++;
      else if (pl === 'ios') counts.ios++;
    });
    return counts;
  }, [properties]);

  const filteredProps = useMemo(() => {
    return properties.filter(p => {
      const pl = (p.platform || 'android').toLowerCase();
      const matchPlatform = platform === 'all' || pl === platform;
      const matchSearch   = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.id.includes(search);
      return matchPlatform && matchSearch;
    });
  }, [properties, platform, search]);

  const handleSelect = (id) => {
    onPropChange(id);
    setAppOpen(false);
    setSearch('');
  };

  const platformIconName = (pl) => pl === 'ios' ? 'bxl-apple' : 'bxl-android';
  const platformLabel    = (pl) => pl === 'ios' ? 'iOS' : 'Android';

  return (
    <>
      {mobileOpen && (
        <div
          onClick={onMobileClose}
          style={styles.backdrop}
          className="sidebar-backdrop"
        />
      )}
      <aside
        style={styles.sidebar}
        className={`app-sidebar${mobileOpen ? ' open' : ''}`}
      >

      {/* ── Brand ───────────────────────────────── */}
      <div style={styles.brand}>
        <div style={{ ...styles.brandIconWrap, background: accentColor + '22', border: `1px solid ${accentColor}44` }}>
          <i className="bx bx-bar-chart-alt-2" style={{ ...styles.brandIcon, color: accentColor }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.brandName}>Analytics</div>
          <div style={styles.brandSub}>Dashboard Pro</div>
        </div>
        <button style={styles.themeBtn} onClick={onToggleTheme} title="Toggle theme">
          <i className={`bx ${theme === 'dark' ? 'bx-sun' : 'bx-moon'}`} style={{ fontSize: 14 }} />
        </button>
        <button
          style={styles.themeBtn}
          className="sidebar-close-btn"
          onClick={onMobileClose}
          title="Close menu"
        >
          <i className="bx bx-x" style={{ fontSize: 16 }} />
        </button>
      </div>

      {/* ── Platform Picker ─────────────────────── */}
      <div style={styles.platformSection}>
        <div style={styles.sectionLabel}>PLATFORM</div>
        <div style={styles.platformRow}>
          {PLATFORMS.map(pl => {
            const isActive = platform === pl.id;
            const count    = platformCounts[pl.id] || 0;
            return (
              <button
                key={pl.id}
                onClick={() => { setPlatform(pl.id); setSearch(''); }}
                style={{
                  ...styles.platformBtn,
                  ...(isActive ? {
                    background: accentColor + '22',
                    borderColor: accentColor + '88',
                    color: accentColor,
                  } : {}),
                }}
              >
                <i className={`bx ${pl.icon}`} style={styles.platformIcon} />
                <span style={styles.platformLabel}>{pl.label}</span>
                <span style={{
                  ...styles.platformBadge,
                  background: isActive ? accentColor + '33' : 'var(--surface3)',
                  color: isActive ? accentColor : 'var(--text3)',
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── App Selector ────────────────────────── */}
      {properties.length > 0 && (
        <div style={styles.appSection}>
          <div style={styles.sectionLabel}>ACTIVE APP</div>

          <button
            style={{ ...styles.appCard, borderColor: accentColor + '66', background: accentColor + '0e' }}
            onClick={() => setAppOpen(o => !o)}
          >
            <i
              className={`bx ${platformIconName(currentProp?.platform)}`}
              style={{ ...styles.appCardPlatformIcon, color: accentColor }}
            />
            <div style={styles.appCardText}>
              <div style={{ ...styles.appCardName, color: accentColor }}>{currentProp?.name || 'Select App'}</div>
              <div style={styles.appCardMeta}>
                <span style={{ ...styles.appPlatformTag, color: accentColor + 'cc' }}>{platformLabel(currentProp?.platform)}</span>
                <span style={styles.appCardId}>· {currentProp?.id}</span>
              </div>
            </div>
            <i
              className="bx bx-chevron-down"
              style={{ ...styles.appChevron, transform: appOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </button>

          {appOpen && (
            <div style={styles.dropdown}>
              <div style={styles.searchWrap}>
                <i className="bx bx-search" style={styles.searchIcon} />
                <input
                  style={styles.searchInput}
                  placeholder="Search apps..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <button style={styles.clearBtn} onClick={() => setSearch('')}>
                    <i className="bx bx-x" />
                  </button>
                )}
              </div>

              <div style={styles.dropdownList}>
                {filteredProps.length === 0 ? (
                  <div style={styles.noResult}>No apps found</div>
                ) : filteredProps.map((p) => {
                  const globalI = properties.findIndex(x => x.id === p.id);
                  const c       = APP_COLORS[globalI % APP_COLORS.length];
                  const isActive = p.id === selectedProp;
                  const pl       = (p.platform || 'android').toLowerCase();
                  return (
                    <button
                      key={p.id}
                      style={{
                        ...styles.appOption,
                        background: isActive ? c + '1a' : 'transparent',
                        borderLeft: `3px solid ${isActive ? c : 'transparent'}`,
                      }}
                      onClick={() => handleSelect(p.id)}
                    >
                      <i
                        className={`bx ${platformIconName(pl)}`}
                        style={{ ...styles.optPlatformIcon, color: isActive ? c : 'var(--text2)' }}
                      />
                      <div style={styles.optTextWrap}>
                        <div style={{ ...styles.optName, color: isActive ? c : 'var(--text)' }}>{p.name}</div>
                        <div style={styles.optMeta}>
                          <span style={{ ...styles.optPlatformBadge, background: c + '22', color: c }}>
                            {platformLabel(pl)}
                          </span>
                          <span style={styles.optId}>{p.id}</span>
                        </div>
                      </div>
                      {isActive && <i className="bx bx-check" style={{ color: c, fontSize: 14, marginLeft: 'auto', flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Nav Links ───────────────────────────── */}
      <nav style={styles.nav}>
        {nav.map(item => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              style={{
                ...styles.navBtn,
                ...(isActive ? { background: accentColor + '18', color: accentColor } : {}),
              }}
              onClick={() => { onChange(item.id); setAppOpen(false); onMobileClose && onMobileClose(); }}
            >
              <i className={`bx ${item.icon}`} style={styles.navIcon} />
              <span style={styles.navLabel}>{item.label}</span>
              {isActive && <span style={{ ...styles.navDot, background: accentColor, boxShadow: `0 0 8px ${accentColor}` }} />}
            </button>
          );
        })}
      </nav>

      {/* ── Footer ──────────────────────────────── */}
      <div style={styles.footer}>
        <div style={styles.footerDot} />
        <span style={styles.footerText}>
          GA4 · {properties.length} app{properties.length !== 1 ? 's' : ''}
        </span>
      </div>
    </aside>
    </>
  );
}

const styles = {
  sidebar: {
    width: 200,
    minHeight: '100vh',
    background: 'var(--sidebar-bg)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 0',
    position: 'fixed',
    top: 0, left: 0,
    zIndex: 100,
    overflowY: 'auto',
    transition: 'background 0.25s, border-color 0.25s',
  },

  // ── brand
  brand: {
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '0 10px 12px',
    borderBottom: '1px solid var(--border)',
  },
  brandIconWrap: {
    width: 30, height: 30, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  brandIcon: { fontSize: 17 },
  brandName: { fontWeight: 700, fontSize: 12, color: 'var(--text)', letterSpacing: '-0.3px' },
  brandSub:  { fontSize: 8, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase' },
  themeBtn: {
    background: 'none', border: '1px solid var(--border)',
    borderRadius: 6, width: 26, height: 26,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, color: 'var(--text2)', padding: 0,
  },

  // ── section label
  sectionLabel: {
    fontSize: 8, color: 'var(--text3)', fontWeight: 700,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    marginBottom: 6, paddingLeft: 2,
  },

  // ── platform
  platformSection: {
    padding: '10px 10px 8px',
    borderBottom: '1px solid var(--border)',
  },
  platformRow: { display: 'flex', gap: 4 },
  platformBtn: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '6px 3px', borderRadius: 8,
    background: 'var(--surface2)', border: '1px solid var(--border)',
    cursor: 'pointer', transition: 'all 0.15s', color: 'var(--text2)',
  },
  platformIcon:  { fontSize: 14 },
  platformLabel: { fontSize: 8, fontWeight: 700, letterSpacing: '0.05em' },
  platformBadge: {
    fontSize: 8, fontWeight: 700, padding: '1px 4px',
    borderRadius: 7, minWidth: 14, textAlign: 'center',
  },

  // ── app selector
  appSection: {
    padding: '10px 10px 8px',
    borderBottom: '1px solid var(--border)',
  },
  appCard: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
    padding: '7px 9px', borderRadius: 9, border: '1px solid',
    cursor: 'pointer', background: 'transparent',
    transition: 'all 0.15s', textAlign: 'left',
  },
  appCardPlatformIcon: { fontSize: 16, flexShrink: 0 },
  appCardText: { flex: 1, minWidth: 0 },
  appCardName: {
    fontSize: 12, fontWeight: 700, letterSpacing: '-0.2px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  appCardMeta: { display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 },
  appPlatformTag: { fontSize: 9, fontWeight: 600 },
  appCardId: { fontSize: 9, color: 'var(--text3)', fontFamily: 'Space Mono, monospace' },
  appChevron: { color: 'var(--text3)', fontSize: 14, flexShrink: 0, transition: 'transform 0.2s' },

  // ── dropdown
  dropdown: {
    marginTop: 6,
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 8px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface)',
  },
  searchIcon: { fontSize: 13, color: 'var(--text3)', flexShrink: 0 },
  searchInput: {
    flex: 1, background: 'none', border: 'none', outline: 'none',
    color: 'var(--text)', fontSize: 11, fontFamily: 'Sora, sans-serif',
    minWidth: 0,
  },
  clearBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text3)', fontSize: 13, padding: 0,
    display: 'flex', alignItems: 'center',
  },
  dropdownList: { maxHeight: 200, overflowY: 'auto' },
  noResult: {
    padding: '12px 10px', fontSize: 11,
    color: 'var(--text3)', textAlign: 'center',
  },
  appOption: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
    padding: '7px 9px', border: 'none', cursor: 'pointer',
    textAlign: 'left', transition: 'all 0.15s',
  },
  optPlatformIcon: { fontSize: 15, flexShrink: 0 },
  optTextWrap: { flex: 1, minWidth: 0 },
  optName: { fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  optMeta: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 },
  optPlatformBadge: {
    fontSize: 8, fontWeight: 700, padding: '1px 5px',
    borderRadius: 5, letterSpacing: '0.04em',
  },
  optId: { fontSize: 8, color: 'var(--text3)', fontFamily: 'Space Mono, monospace' },

  // ── nav
  nav: { flex: 1, padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: 1 },
  navBtn: {
    display: 'flex', alignItems: 'center', gap: 9, width: '100%',
    padding: '7px 10px', background: 'none', border: 'none', borderRadius: 8,
    color: 'var(--text2)', fontSize: 12, fontWeight: 500, textAlign: 'left',
    transition: 'all 0.15s', cursor: 'pointer', position: 'relative',
  },
  navIcon: { fontSize: 16, width: 18, textAlign: 'center', flexShrink: 0 },
  navLabel: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  navDot:  { position: 'absolute', right: 10, width: 5, height: 5, borderRadius: '50%' },

  // ── footer
  footer: {
    padding: '10px 12px', borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', gap: 7,
  },
  footerDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--success)', boxShadow: '0 0 8px var(--success)', flexShrink: 0,
  },
  footerText: { fontSize: 9, color: 'var(--text3)' },
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 90, backdropFilter: 'blur(2px)',
  },
};
